import React, { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import HtmlParser from 'components/Assessment/HtmlParser'
import RightExpand from 'assets/icons/right-expand.svg'
import LeftExpand from 'assets/icons/left-expand.svg'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  AssignmentBodyItem,
  AssignmentPage,
  QuestionBodyItem
} from 'interfaces/assignments'
import {
  SelectAssignmentState,
  setAssignmentPageId,
  StudentSubmissionAnswer
} from 'app/reducer/assignmentSlice'
import LinkedReferenceSection from './LinkedReferenceSection'

import { ContentWrapper, HideAllButton, QuestionPanel } from '../../styles'
import {
  getQuestionChoiceOptions,
  isChoiceEmpty,
  QuestionChoiceOption
} from '../../utils'
import Choice from 'components/Assessment/Choice2'
import StimulusArea from 'components/StimulusArea/StimulusArea'
import {
  TabHeaderActionButton,
  TabHeaderActionIcon
} from 'components/StimulusArea/styles'

const isQuestionItem = (
  item: AssignmentBodyItem | QuestionBodyItem
): item is QuestionBodyItem => {
  return (
    item.body_item_type === 'question' &&
    'choices' in item &&
    'question_text' in item &&
    'answer' in item
  )
}

const sanitizeQuestionTextForInline = (questionText: string = '') => {
  const normalized = questionText.trim()

  /**
   * Convert a single wrapping paragraph into a span so question numbers can be prefixed inline.
   * - /^<p(\s[^>]*)?>/i matches an opening <p> tag at the start (including optional attributes)
   * - /<\/p>$/i matches a closing </p> tag at the end
   * The `i` flag makes matching case-insensitive (e.g., <P>...</P>).
   */
  return normalized
    .replace(/^<p(\s[^>]*)?>/i, '<span$1>')
    .replace(/<\/p>$/i, '</span>')
}

interface QuestionSetContainerProps {
  bodyItems: AssignmentBodyItem[] | QuestionBodyItem[]
  readOnly: boolean
  referencedSection?: AssignmentPage
  stimulusSourcePage?: AssignmentPage | null
  onAnswerChange: (questionId: number, selectedChoice: number) => void
}

const QuestionSetContainer = ({
  readOnly,
  referencedSection,
  bodyItems,
  stimulusSourcePage,
  onAnswerChange
}: QuestionSetContainerProps) => {
  /**
   * This component will only render the "Question Set" page of the assignment.
   * A "Question Set" page may fulfill the following conditions:
   * 1. It may contain a set of questions that are grouped together under a common theme or topic.
   * 2. Can include a Stimulus, which is a passage of text, image, or video that provides context for the questions in the set.
   * 3. Can link to a previous Section
   * 4. can link only to the immediately previous Question Set to create a shared Question Set grouping
   * Additionally we will also support student / teacher modes if user wants to see how the authored
   * content will look like for the students
   */
  const dispatch = useAppDispatch()
  const {
    assignment,
    currentPage,
    enableStudentSubmissionView,
    currentAttempt
  } = useAppSelector(SelectAssignmentState)

  const studentAnswerByQuestionId = useMemo(() => {
    const map: Record<number, number> = {}

    if (!currentAttempt?.answers?.length) {
      return map
    }

    currentAttempt.answers.forEach((answer: StudentSubmissionAnswer) => {
      const parsedAnswer = Number(answer.answer)

      if (!Number.isNaN(parsedAnswer)) {
        map[answer.question_id] = parsedAnswer
      }
    })

    return map
  }, [currentAttempt])

  const onViewSection = () => {
    if (!referencedSection) return
    dispatch(setAssignmentPageId(referencedSection.id))
  }

  const [hiddenChoicesByQuestion, setHiddenChoicesByQuestion] = useState<
    Record<number, Record<string, boolean>>
  >({})
  const [activeTab, setActiveTab] = useState<'tab1' | 'tab2'>('tab1')
  const [questionVisible, setQuestionVisible] = useState(true)
  const [stimulusVisible, setStimulusVisible] = useState(true)

  /**
   * Each assignment page starts from the normal split layout so pages with
   * fewer controls cannot inherit a hidden state they cannot reverse.
   */
  useEffect(() => {
    setQuestionVisible(true)
    setStimulusVisible(true)
  }, [currentPage?.id])

  const effectiveStimulusSourcePage = stimulusSourcePage || currentPage

  const stimulusTabOne = effectiveStimulusSourcePage?.stimulus_tab_one || ''
  const stimulusTabTwo = effectiveStimulusSourcePage?.stimulus_tab_two || ''

  const hasStimulusTabOneData = !!stimulusTabOne.trim()
  const hasStimulusTabTwoData = !!stimulusTabTwo.trim()

  const shouldShowStimulusTabs = hasStimulusTabOneData || hasStimulusTabTwoData

  /**
   * Each arrow is disabled when its transition would not change the currently
   * visible panels.
   */
  const isLeftArrowDisabled = questionVisible && !stimulusVisible
  const isRightArrowDisabled = !questionVisible && stimulusVisible

  const questionNumberOffset = useMemo(() => {
    if (!assignment?.pages || !currentPage) {
      return 0
    }

    return assignment.pages
      .filter(
        (page: AssignmentPage) =>
          page.page_type === 'question_set' &&
          page.order_index < currentPage.order_index
      )
      .reduce((totalQuestions: number, page: AssignmentPage) => {
        const questionCount = page.body_items.filter(
          (item: AssignmentBodyItem | QuestionBodyItem) => isQuestionItem(item)
        ).length

        return totalQuestions + questionCount
      }, 0)
  }, [assignment?.pages, currentPage])

  /**
   * The two visibility flags let either half of the assignment take the full
   * viewer width. The same arrow then restores the opposite panel, returning
   * the assignment to its normal side-by-side layout.
   */
  function onLeftArrowClick() {
    if (isLeftArrowDisabled) return

    if (questionVisible && stimulusVisible) {
      return setStimulusVisible(false)
    }

    if (!questionVisible && stimulusVisible) {
      setQuestionVisible(true)
    }
  }

  function onRightArrowClick() {
    if (isRightArrowDisabled) return

    if (questionVisible && !stimulusVisible) {
      setStimulusVisible(true)
    }

    if (questionVisible && stimulusVisible) {
      setQuestionVisible(false)
    }
  }

  /**
   * A question's Hide All button updates all of its visible choices together.
   * Individual choice updates stay in this map so the label reflects the
   * current state after either a bulk or individual action.
   */
  const toggleHideAllForQuestion = (
    questionId: number,
    choices: QuestionChoiceOption[]
  ) => {
    setHiddenChoicesByQuestion((prev) => {
      const hiddenChoices = prev[questionId] || {}
      const areAllChoicesHidden =
        choices.length > 0 &&
        choices.every((choice) => hiddenChoices[choice.key])
      const nextHidden = !areAllChoicesHidden

      return {
        ...prev,
        [questionId]: choices.reduce<Record<string, boolean>>(
          (nextChoices, choice) => ({
            ...nextChoices,
            [choice.key]: nextHidden
          }),
          {}
        )
      }
    })
  }

  const renderBodyItems = () => {
    let questionNumber = questionNumberOffset

    return bodyItems.map((item) => {
      if (isQuestionItem(item)) {
        questionNumber += 1
        const displayQuestionNumber = questionNumber
        const choices = getQuestionChoiceOptions(item.choices)
        /**
         * Question-set rendering must mirror the assessment/CMS empty-html rule so
         * authored blank wrappers never produce visible answer choices for students.
         */
        const visibleChoices = choices.filter(
          (choice) => !isChoiceEmpty(choice.value)
        )
        const hiddenChoices = hiddenChoicesByQuestion[item.id] || {}
        const areAllChoicesHidden =
          visibleChoices.length > 0 &&
          visibleChoices.every((choice) => hiddenChoices[choice.key])

        return (
          <div
            key={item.id}
            data-assignment-question='true'
            data-question-order-index={item.order_index}
            data-question-id={item.id}
            className={clsx({
              'contrast-area': !enableStudentSubmissionView
            })}
          >
            <div
              className={clsx('questions__question-item instruction', {
                'contrast-area': enableStudentSubmissionView
              })}
              style={{
                marginBottom: 14
              }}
            >
              <div className='d-flex'>
                <div className={clsx('questions__question-instruction')}>
                  <HtmlParser
                    html={`<span>${displayQuestionNumber}. </span>${sanitizeQuestionTextForInline(item.question_text)}`}
                  />
                </div>
              </div>
            </div>

            <HideAllButton
              className={clsx({
                'contrast-area': enableStudentSubmissionView
              })}
              disabled={enableStudentSubmissionView}
              style={{ marginBottom: 15 }}
              onClick={() =>
                toggleHideAllForQuestion(item.id, visibleChoices)
              }
            >
              {areAllChoicesHidden ? 'Show All' : 'Hide All'}
            </HideAllButton>

            {visibleChoices.map((choice) => {
              return (
                <div
                  key={`${item.id}-${choice.key}`}
                  className='questions__question-item'
                >
                  <Choice
                    choice={choice.code}
                    choiceText={choice.value}
                    option={choice.answerValue}
                    answer={Number.isInteger(item.answer) ? item.answer : -1}
                    onClick={() => onAnswerChange(item.id, choice.answerValue)}
                    studentAnswer={
                      enableStudentSubmissionView
                        ? studentAnswerByQuestionId[item.id]
                        : undefined
                    }
                    choiceId={`choice-${item.id}-${choice.key}`}
                    showIcons
                    hidden={Boolean(hiddenChoices[choice.key])}
                    onHiddenChange={(hidden) =>
                      setHiddenChoicesByQuestion((prev) => ({
                        ...prev,
                        [item.id]: {
                          ...prev[item.id],
                          [choice.key]: hidden
                        }
                      }))
                    }
                    preview
                  />
                </div>
              )
            })}
          </div>
        )
      }

      return (
        <div
          key={item.id}
          className={clsx({
            'contrast-area': !enableStudentSubmissionView
          })}
        >
          <div>
            <HtmlParser
              html={item.content_html}
              id={`assignment-body-${item.id}`}
            />
          </div>
        </div>
      )
    })
  }

  return (
    <span>
      {referencedSection && (
        <LinkedReferenceSection
          readOnly={readOnly}
          pageNumber={referencedSection.order_index}
          sectionLabel={referencedSection.title}
          referenceSectionTitle={currentPage?.reference_section_title}
          onViewSection={onViewSection}
          disableViewSection={referencedSection.is_disabled}
        />
      )}

      {shouldShowStimulusTabs ? (
        <ContentWrapper>
          {stimulusVisible && (
            <StimulusArea
              readOnly={true}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              fullWidth={!questionVisible}
              stimulusTabOne={stimulusTabOne}
              stimulusTabTwo={stimulusTabTwo}
              onLeftArrowClick={onLeftArrowClick}
              onRightArrowClick={onRightArrowClick}
              isLeftArrowDisabled={isLeftArrowDisabled}
              isRightArrowDisabled={isRightArrowDisabled}
            />
          )}

          {questionVisible && (
            <QuestionPanel
              $fullWidth={!stimulusVisible}
              className='contrast-area'
            >
              {!stimulusVisible && (
                <div style={{ paddingLeft: 10, paddingRight: 10 }}>
                  <TabHeaderActionButton style={{ position: 'relative' }}>
                    <TabHeaderActionIcon
                      $disabled={isRightArrowDisabled}
                      aria-disabled={isRightArrowDisabled}
                      onClick={
                        isRightArrowDisabled ? undefined : onRightArrowClick
                      }
                      src={RightExpand}
                      alt='maximize'
                    />
                    <TabHeaderActionIcon
                      $disabled={isLeftArrowDisabled}
                      aria-disabled={isLeftArrowDisabled}
                      onClick={
                        isLeftArrowDisabled ? undefined : onLeftArrowClick
                      }
                      src={LeftExpand}
                      alt='maximize'
                    />
                  </TabHeaderActionButton>
                </div>
              )}
              <span>{renderBodyItems()}</span>
            </QuestionPanel>
          )}
        </ContentWrapper>
      ) : (
        <>{renderBodyItems()}</>
      )}
    </span>
  )
}

export default QuestionSetContainer
